/**
 * Unified API Client Factory
 *
 * Single factory for creating Axios instances with consistent auth, headers,
 * error handling, and request tracing across the entire admin portal.
 *
 * Every client created by this factory gets:
 *   - Bearer token from tokenService (sync cached token)
 *   - X-Institution-ID header (multi-tenant context)
 *   - X-Request-ID header (distributed tracing)
 *   - 401 → tokenService.handleUnauthorized() by default
 *   - Content-Type: application/json
 *   - 30s default timeout
 *
 * USAGE:
 *   import { createApiClient } from '@/services/apiClient';
 *
 *   const api = createApiClient(import.meta.env.VITE_API_URL || '/api');
 *   const gateway = createApiClient(import.meta.env.VITE_GATEWAY_URL, 60000);
 */

import axios, {
  type AxiosInstance,
  type InternalAxiosRequestConfig,
  type AxiosResponse,
  type AxiosError,
} from 'axios';
import { tokenService } from './tokenService';

export interface ApiClientOptions {
  /**
   * Disable only when a 401 does not represent an invalid portal session,
   * such as an endpoint protected by separate service credentials.
   */
  invalidateSessionOnUnauthorized?: boolean;
}

// ---------------------------------------------------------------------------
// Request ID generator
// ---------------------------------------------------------------------------

const generateRequestId = (): string =>
  `req_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;

const hasInstitutionHeader = (
  headers: InternalAxiosRequestConfig['headers'] | undefined,
): boolean => {
  if (!headers) return false;

  return typeof headers['X-Institution-ID'] !== 'undefined'
    || typeof headers['x-institution-id'] !== 'undefined';
};

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

/**
 * Create a configured Axios instance with auth, tenant context, and tracing.
 *
 * @param baseURL  - The base URL for all requests (e.g. import.meta.env.VITE_API_URL)
 * @param timeout  - Request timeout in milliseconds (default: 30 000)
 * @param options  - Per-client authentication error behavior
 */
export function createApiClient(
  baseURL: string,
  timeout = 30_000,
  options: ApiClientOptions = {},
): AxiosInstance {
  const client = axios.create({
    baseURL,
    timeout,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // --- Request interceptor: auth + tenant + tracing -----------------------
  client.interceptors.request.use(
    async (config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
      const institutionId = tokenService.getInstitutionId();
      const token = await tokenService.getAccessToken();
      if (token && config.headers) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }

      if (institutionId && config.headers && !hasInstitutionHeader(config.headers)) {
        config.headers['X-Institution-ID'] = institutionId;
      }

      if (config.headers) {
        config.headers['X-Request-ID'] = generateRequestId();
      }

      return config;
    },
    (error: AxiosError): Promise<AxiosError> => Promise.reject(error),
  );

  // --- Response interceptor: 401 handling ---------------------------------
  client.interceptors.response.use(
    (response: AxiosResponse): AxiosResponse => response,
    (error: AxiosError): Promise<AxiosError> => {
      if (
        error.response?.status === 401 &&
        options.invalidateSessionOnUnauthorized !== false
      ) {
        tokenService.handleUnauthorized();
      }
      return Promise.reject(error);
    },
  );

  return client;
}
